import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getAreaCodeForLocation, isValidState } from '@/lib/stateUtils';
import { isMSACode } from '@/lib/areaUtils';

/**
 * Hook to resolve location slug to area code
 * Handles states, MSAs, and national
 */
export const useAreaCodeResolver = (locationSlug: string) => {
  const stateAreaCode = getAreaCodeForLocation(locationSlug);

  const { data: msaData, isLoading, isError } = useQuery({
    queryKey: ['msa-resolve', locationSlug],
    queryFn: async () => {
      const locationName = locationSlug.replace(/-/g, ' ');

      const { data, error } = await supabase
        .from('vw_agg_year_area_occ')
        .select('area_code, area_name')
        .eq('year', 2025)
        .eq('area_type', '4')
        .ilike('area_name', `%${locationName}%`)
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error resolving MSA:', error);
        return null;
      }

      return data;
    },
    enabled: !!locationSlug && !stateAreaCode && !isValidState(locationSlug),
    staleTime: 1000 * 60 * 60 * 24,
  });

  if (stateAreaCode) {
    return {
      data: stateAreaCode,
      isLoading: false,
      isError: false,
      areaType: stateAreaCode === '99' ? 'national' : 'state' as 'national' | 'state' | 'msa'
    };
  }

  return {
    data: msaData?.area_code || null,
    isLoading,
    isError,
    areaType: msaData?.area_code ? (isMSACode(msaData.area_code) ? 'msa' : 'state') : null as 'national' | 'state' | 'msa' | null
  };
};

